import React from 'react';
import { motion } from 'framer-motion';
import silhouette from '../../assets/silhouette.png';

// Utilitaire pour obtenir le drapeau emoji à partir du code pays (ex: 'FR' => 🇫🇷)
const getFlag = (nationalite) => {
  if (!nationalite) return null;
  // Normalisation : majuscules, trim, accents retirés
  const norm = nationalite.trim().toUpperCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
  // Mapping noms et codes
  const flags = {
    'FRANCE': '🇫🇷', 'FR': '🇫🇷',
    'BELGIQUE': '🇧🇪', 'BE': '🇧🇪',
    'ESPAGNE': '🇪🇸', 'ES': '🇪🇸',
    'ITALIE': '🇮🇹', 'IT': '🇮🇹',
    'ALLEMAGNE': '🇩🇪', 'DE': '🇩🇪',
    'PORTUGAL': '🇵🇹', 'PT': '🇵🇹',
    'MAROC': '🇲🇦', 'MA': '🇲🇦',
    'SENEGAL': '🇸🇳', 'SN': '🇸🇳',
    'CAMEROUN': '🇨🇲', 'CM': '🇨🇲',
    'BRESIL': '🇧🇷', 'BR': '🇧🇷',
    'ANGLETERRE': '🇬🇧', 'EN': '🇬🇧', 'GB': '🇬🇧', 'UK': '🇬🇧',
    'SUISSE': '🇨🇭', 'CH': '🇨🇭',
    'PAYS-BAS': '🇳🇱', 'PAYS BAS': '🇳🇱', 'NL': '🇳🇱',
    'ALGERIE': '🇩🇿', 'DZ': '🇩🇿',
    'TUNISIE': '🇹🇳', 'TN': '🇹🇳',
    'TURQUIE': '🇹🇷', 'TR': '🇹🇷',
    'POLOGNE': '🇵🇱', 'PL': '🇵🇱',
    'USA': '🇺🇸', 'ETATS-UNIS': '🇺🇸', 'US': '🇺🇸',
    'FRANCE (DOM-TOM)': '🇫🇷',
    'COTE D IVOIRE': '🇨🇮', 'CI': '🇨🇮',
    'MALI': '🇲🇱', 'ML': '🇲🇱',
    'NIGER': '🇳🇪', 'NE': '🇳🇪',
    'TOGO': '🇹🇬', 'TG': '🇹🇬',
    'ZAMBIE': '🇿🇲', 'ZM': '🇿🇲',
  };
  return flags[norm] || null;
};

const PlayerCard = ({ joueur, large }) => {
  // Taille de la carte adaptée
  const cardSize = large ? 'w-[400px] h-[500px]' : 'w-[320px] h-[400px]';

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={{ 
        y: -8, 
        scale: 1.02,
        transition: { duration: 0.3 }
      }}
      className={`relative rounded-3xl overflow-hidden ${cardSize} group transition-all duration-500 cursor-pointer`}
      style={{ minWidth: large ? 400 : 320 }}
    >
      {/* Container principal avec glassmorphism */}
      <div className="absolute inset-0 bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl shadow-2xl">
        {/* Image de fond (face_photo ou silhouette) */}
        <div className="absolute inset-0 rounded-3xl overflow-hidden">
          <img
            src={joueur.face_photo || silhouette}
            alt={joueur.nom || joueur.prenom || 'Joueur'}
            className="w-full h-full object-cover object-[center_20%] transition-transform duration-500 group-hover:scale-110"
            draggable={false}
          />
          {/* Overlay dégradé moderne */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#001B56]/90 via-[#001B56]/30 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-transparent to-[#001B56]/40" />
        </div>

        {/* Effets de lumière */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-4 left-4 w-16 h-16 bg-[#FFE400]/20 rounded-full blur-xl opacity-60 group-hover:opacity-80 transition-opacity duration-300" />
          <div className="absolute bottom-4 right-4 w-20 h-20 bg-[#BDFF00]/20 rounded-full blur-xl opacity-40 group-hover:opacity-60 transition-opacity duration-300" />
        </div>

        {/* Poste en haut à gauche */}
        {joueur.poste && (
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="absolute top-4 left-4 z-20"
          >
            <div className="bg-white/20 backdrop-blur-md border border-white/30 text-white px-3 py-1 rounded-xl text-sm font-accent font-bold shadow-lg">
              {joueur.poste.slice(0,3).toUpperCase()}
            </div>
          </motion.div>
        )}

        {/* Drapeau nationalité en haut à droite */}
        {joueur.nationalite && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="absolute top-4 right-4 z-20"
          >
            <div className="bg-white/20 backdrop-blur-md border border-white/30 rounded-xl p-2 shadow-lg">
              <span className="text-2xl drop-shadow-lg">
                {getFlag(joueur.nationalite) || '🌍'}
              </span>
            </div>
            {/* Affiche le code pays si pas de drapeau trouvé */}
            {!getFlag(joueur.nationalite) && (
              <div className="absolute -bottom-6 right-0 bg-[#FFE400] text-[#001B56] px-2 py-1 rounded-lg text-xs font-bold">
                {joueur.nationalite}
              </div>
            )}
          </motion.div>
        )}

        {/* Numéro watermark géant */}
        {joueur.numero && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="absolute bottom-16 right-4 z-10"
          >
            <span 
              className="text-[6rem] font-extrabold text-[#FFE400]/20 select-none pointer-events-none leading-none group-hover:text-[#FFE400]/30 transition-colors duration-300" 
              style={{lineHeight:'0.9'}}
            >
              {joueur.numero}
            </span>
          </motion.div>
        )}

        {/* Informations du joueur en bas */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="absolute bottom-0 left-0 w-full z-20"
        >
          {/* Background glassmorphism pour le texte */}
          <div className="bg-white/10 backdrop-blur-md border-t border-white/20 p-4 rounded-b-3xl">
            <div className="flex items-end justify-between">
              <div className="flex-1">
                {joueur.prenom && (
                  <div className="text-[#FFE400] font-accent font-bold text-lg leading-tight mb-1">
                    {joueur.prenom}
                  </div>
                )}
                {joueur.nom && (
                  <div className="text-white font-title font-extrabold text-xl leading-tight">
                    {joueur.nom.toUpperCase()}
                  </div>
                )}
                
                {/* Statistiques si disponibles */}
                {(joueur.buts > 0 || joueur.passes > 0) && (
                  <div className="flex gap-3 mt-2">
                    {joueur.buts > 0 && (
                      <div className="bg-[#BDFF00]/20 backdrop-blur-sm border border-[#BDFF00]/30 rounded-lg px-2 py-1">
                        <span className="text-[#BDFF00] text-xs font-accent font-bold">
                          ⚽ {joueur.buts}
                        </span>
                      </div>
                    )}
                    {joueur.passes > 0 && (
                      <div className="bg-[#FFE400]/20 backdrop-blur-sm border border-[#FFE400]/30 rounded-lg px-2 py-1">
                        <span className="text-[#FFE400] text-xs font-accent font-bold">
                          🎯 {joueur.passes}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Badge numéro */}
              {joueur.numero && (
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  className="bg-gradient-to-br from-[#FFE400] to-[#BDFF00] text-[#001B56] rounded-2xl px-4 py-2 shadow-lg ml-3"
                >
                  <span className="font-title font-extrabold text-lg">
                    #{joueur.numero}
                  </span>
                </motion.div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Effet de brillance au hover */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out" />
      </div>
    </motion.div>
  );
};

export default PlayerCard; 